/**
 * Quick-View Modal v10 — Moonwater
 * "Defuse and Reuse" approach
 *
 * KEY INSIGHT: Dawn registers custom elements globally (product-info, variant-radios,
 * product-form, etc.). When we inject fetched HTML containing these tags, the browser
 * auto-upgrades them and Dawn's JS hijacks all events (variant clicking, form submission,
 * section rendering). This breaks everything in a modal context.
 *
 * SOLUTION: Before injecting into the DOM, rename ALL custom element tags to plain <div>s.
 * This preserves classes, attributes, children — but prevents auto-upgrade.
 * Then wire our own simple JS for variants, cart, and Seal re-init.
 */
(function () {
  'use strict';

  var OVERLAY_SEL = '#QuickViewOverlay';
  var cache = {};

  /* ── Custom elements to defuse ── */
  var CUSTOM_ELEMENTS = [
    'product-info', 'variant-radios', 'variant-selects', 'product-form',
    'quantity-input', 'media-gallery', 'slider-component', 'deferred-media',
    'price-per-item', 'volume-pricing', 'show-more-button', 'modal-opener',
    'modal-dialog', 'slideshow-component', 'product-recommendations'
  ];

  /* ── Elements to remove from modal ── */
  var REMOVE_SELECTORS = [
    '.mw-sticky-atc',
    '.product__description',
    '.product__accordion',
    '.installment',
    '.product__sku',
    '.product__inventory',
    '.pickup-availabilities-container',
    '.share-button',
    '.flavor-selector-wrapper',
    '.product-popup-modal',
    '.product-media-modal',
    '[id^="ProductModal"]',
    '.complementary-products',
    '.icon-with-text',
    '.product__view-details',
    'a.product__view-details',
    '.link.product__view-details',
    '[id^="Quantity-Form-"]',
    '.quantity-button-inline-container',
    '.quantity__rules',
    '.shopify-payment-button',
    '.product-popup-modal__opener',
    'noscript',
    // Remove Dawn's variant picker — we build our own from JSON
    '.product-form__input--pill',
    '.product-form__input--swatch',
    '[data-option-name]'
  ];

  function lockScroll() { document.body.style.overflow = 'hidden'; }
  function unlockScroll() { document.body.style.overflow = ''; }
  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

  /* ── Defuse: rename custom elements to plain <div>s ── */
  function defuseCustomElements(root) {
    var ownerDoc = root.ownerDocument || document;
    CUSTOM_ELEMENTS.forEach(function (tag) {
      // querySelectorAll on parsed doc works with tag names
      var els = root.querySelectorAll(tag);
      // Process in reverse to handle nesting correctly
      for (var i = els.length - 1; i >= 0; i--) {
        var el = els[i];
        var div = ownerDoc.createElement('div');
        // Copy ALL attributes (class, id, data-*, etc.)
        for (var j = 0; j < el.attributes.length; j++) {
          try { div.setAttribute(el.attributes[j].name, el.attributes[j].value); }
          catch (e) { /* skip invalid */ }
        }
        // Add marker class
        div.classList.add('qv-defused');
        // Move ALL children
        while (el.firstChild) div.appendChild(el.firstChild);
        // Replace in DOM
        if (el.parentNode) el.parentNode.replaceChild(div, el);
      }
    });
  }

  /* ── Clean: remove unwanted elements ── */
  function cleanupElements(root) {
    REMOVE_SELECTORS.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { el.remove(); });
    });
    // Remove all <script> tags
    root.querySelectorAll('script').forEach(function (s) { s.remove(); });
    // Remove all inline <style> tags (we use our own CSS)
    root.querySelectorAll('style').forEach(function (s) { s.remove(); });
    // Force eager loading on all images
    root.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.setAttribute('loading', 'eager');
    });
    // Kill animations
    root.querySelectorAll('.scroll-trigger, .animate--slide-in').forEach(function (el) {
      el.style.animation = 'none';
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /* ── Extract product data from JSON scripts ── */
  function extractProductData(doc) {
    var data = { variants: [], images: [], options: [], currentVariant: null };

    // Find product JSON in script tags
    doc.querySelectorAll('script[type="application/json"]').forEach(function (script) {
      try {
        var parsed = JSON.parse(script.textContent);
        // Full product object (has .variants array and .media)
        if (parsed && parsed.variants && Array.isArray(parsed.variants)) {
          data.variants = parsed.variants;
          data.options = parsed.options || [];
          if (parsed.media) {
            data.images = parsed.media
              .filter(function (m) { return m.media_type === 'image'; })
              .map(function (m) {
                return {
                  src: m.src || (m.preview_image && m.preview_image.src),
                  alt: m.alt || ''
                };
              });
          }
          if (!data.images.length && parsed.images) {
            data.images = parsed.images.map(function (img) {
              return { src: typeof img === 'string' ? img : img.src, alt: '' };
            });
          }
        }
        // Variants-only array
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].id && parsed[0].price !== undefined) {
          if (!data.variants.length) data.variants = parsed;
        }
      } catch (e) { /* not JSON, skip */ }
    });

    // Also try parsing inline scripts for variant data (Dawn sometimes embeds it)
    if (!data.variants.length) {
      doc.querySelectorAll('script:not([type])').forEach(function (script) {
        if (data.variants.length) return;
        var text = script.textContent || '';
        // Look for "variants = [...]" or "variants: [...]"
        var m = text.match(/variants\s*[:=]\s*(\[[\s\S]*?\]);/);
        if (m) {
          try { data.variants = JSON.parse(m[1]); } catch (e) {}
        }
      });
    }

    // Fallback: extract images from DOM
    if (!data.images.length) {
      var seen = {};
      doc.querySelectorAll('.product__media-item img, .product__media img, .media img').forEach(function (img) {
        var src = img.getAttribute('src') || '';
        if (src && !seen[src]) {
          seen[src] = true;
          data.images.push({ src: src, alt: img.getAttribute('alt') || '' });
        }
      });
    }

    // Current variant
    var variantInput = doc.querySelector('input[name="id"][type="hidden"]');
    if (variantInput && data.variants.length) {
      var vid = parseInt(variantInput.value);
      data.currentVariant = data.variants.find(function (v) { return v.id === vid; }) || data.variants[0];
    } else if (data.variants.length) {
      data.currentVariant = data.variants[0];
    }

    console.log('[QuickView] Product data:', data.variants.length, 'variants,', data.images.length, 'images');
    return data;
  }

  /* ── Fetch product data from Shopify .js endpoint (most reliable) ── */
  async function fetchProductJson(productUrl) {
    var cleanUrl = productUrl.replace(/\/+$/, '');
    try {
      var res = await fetch(cleanUrl + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        console.warn('[QuickView] .js fetch returned', res.status);
        return null;
      }
      var product = await res.json();

      console.log('[QuickView] .js raw options:', JSON.stringify(product.options));
      console.log('[QuickView] .js variants count:', (product.variants || []).length);

      // Normalize options — Shopify can return:
      // ["Quantity"] or [{name:"Quantity", position:1, values:[...]}]
      var options = (product.options || []).map(function (o) {
        if (typeof o === 'string') return o;
        if (o && o.name) return o.name;
        return String(o);
      });

      var data = {
        variants: product.variants || [],
        options: options,
        images: [],
        currentVariant: null
      };

      // Images from media array
      if (product.media && product.media.length) {
        data.images = product.media
          .filter(function (m) { return m.media_type === 'image'; })
          .map(function (m) {
            return {
              src: m.src || (m.preview_image && m.preview_image.src),
              alt: m.alt || ''
            };
          });
      }
      // Fallback: images array (just URLs)
      if (!data.images.length && product.images) {
        data.images = product.images.map(function (src) {
          return { src: typeof src === 'string' ? src : (src.src || ''), alt: '' };
        });
      }

      if (data.variants.length) data.currentVariant = data.variants[0];

      console.log('[QuickView] .js parsed:', data.variants.length, 'variants,', data.options.length, 'options (' + data.options.join(', ') + '),', data.images.length, 'images');
      return data;
    } catch (e) {
      console.warn('[QuickView] .js fetch error:', e.message);
      return null;
    }
  }

  /* ── Force transparent backgrounds ── */
  function forceTransparentBGs(container) {
    // Target ALL elements that Dawn might paint backgrounds on
    var selectors = [
      '.gradient', '[class*="color-scheme"]', '[class*="color-"]',
      '[class*="section-"]', '.product', '.product__media-wrapper',
      '.product__info-wrapper', '.product__info-container',
      '.page-width', '.grid', '.grid__item', '.qv-product-wrap'
    ];
    selectors.forEach(function (sel) {
      container.querySelectorAll(sel).forEach(function (el) {
        el.style.setProperty('background', 'transparent', 'important');
        el.style.setProperty('background-color', 'transparent', 'important');
        el.style.setProperty('background-image', 'none', 'important');
      });
    });
    // Also set on the container itself
    container.style.setProperty('background', 'transparent', 'important');
  }

  /* ── Open / Close ── */
  function openModal() {
    var o = document.querySelector(OVERLAY_SEL); if (!o) return;
    o.classList.add('is-open'); o.setAttribute('aria-hidden', 'false'); lockScroll();
    setTimeout(function () { var b = o.querySelector('.qv-close'); if (b) b.focus(); }, 100);
  }

  function closeModal() {
    var o = document.querySelector(OVERLAY_SEL); if (!o) return;
    o.classList.remove('is-open'); o.setAttribute('aria-hidden', 'true'); unlockScroll();
    setTimeout(function () {
      var c = o.querySelector('.qv-content'); if (c) c.innerHTML = '';
      var l = o.querySelector('.qv-loader'); if (l) l.classList.remove('hidden');
    }, 350);
  }

  /* ── Build Image Slider ── */
  function buildImageSlider(container, images) {
    if (!images || !images.length) return;
    var mw = container.querySelector('.product__media-wrapper');
    if (!mw) return;

    // Hide Dawn's gallery
    mw.querySelectorAll('.product__media-list, .thumbnail-list, [id^="GalleryThumbnails"], [id^="Slider-"]').forEach(function (el) {
      el.style.display = 'none';
    });
    // Also hide any defused media-gallery or slider-component
    mw.querySelectorAll('.qv-defused').forEach(function (el) {
      el.style.display = 'none';
    });

    var h = '<div class="qv-slider"><div class="qv-slider__main"><img src="' + images[0].src + '" alt="' + (images[0].alt || 'Product') + '" /></div>';
    if (images.length > 1) {
      h += '<div class="qv-slider__thumbs">';
      images.forEach(function (img, i) {
        h += '<button class="qv-slider__thumb' + (i === 0 ? ' is-active' : '') + '" data-index="' + i + '" type="button"><img src="' + img.src + '" alt="' + (img.alt || '') + '" loading="eager" /></button>';
      });
      h += '</div>';
    }
    h += '</div>';
    mw.insertAdjacentHTML('afterbegin', h);

    // Thumb click handlers
    var mainImg = mw.querySelector('.qv-slider__main img');
    var thumbs = mw.querySelectorAll('.qv-slider__thumb');
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var idx = parseInt(t.dataset.index);
        if (images[idx]) {
          mainImg.src = images[idx].src;
          mainImg.alt = images[idx].alt || '';
        }
        thumbs.forEach(function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
      });
    });
  }

  /* ── Build Variant Picker from scratch ── */
  function buildVariantPicker(container, productData, preselect) {
    console.log('[QuickView] buildVariantPicker called with:', {
      variantsCount: productData.variants ? productData.variants.length : 0,
      options: productData.options,
      currentVariant: productData.currentVariant ? productData.currentVariant.title : 'none',
      preselect: preselect || 'none'
    });

    if (!productData.variants || !productData.variants.length) {
      console.warn('[QuickView] No variants — skipping picker');
      return;
    }
    if (productData.variants.length === 1 && productData.variants[0].title === 'Default Title') {
      console.log('[QuickView] Only default variant — skipping picker');
      return;
    }

    var options = productData.options || [];
    if (!options.length) {
      console.warn('[QuickView] No option names — skipping picker');
      return;
    }

    // Derive unique values per option from variants
    var optionValues = options.map(function (optName, optIdx) {
      var seen = {};
      var values = [];
      productData.variants.forEach(function (v) {
        if (v.options && v.options[optIdx] && !seen[v.options[optIdx]]) {
          seen[v.options[optIdx]] = true;
          values.push(v.options[optIdx]);
        }
      });
      return { name: optName, values: values };
    });

    // Calculate SAVE % badges (per-can savings vs 4-pack baseline)
    var savePcts = {};
    var qtyOptIdx = options.findIndex(function (n) { return n.toLowerCase() === 'quantity'; });
    if (qtyOptIdx !== -1) {
      var qtyMinPrice = {};
      productData.variants.forEach(function (v) {
        var label = (v.options[qtyOptIdx] || '').toString().toLowerCase();
        var m = label.match(/(\d+)/);
        if (!m) return;
        var q = parseInt(m[1], 10);
        if (!qtyMinPrice[q] || v.price < qtyMinPrice[q]) qtyMinPrice[q] = v.price;
      });
      var base4 = qtyMinPrice[4];
      var basePerCan = base4 ? base4 / 4 : null;
      if (basePerCan) {
        Object.keys(qtyMinPrice).forEach(function (q) {
          q = parseInt(q);
          if (q === 4) return;
          var fullAtBase = basePerCan * q;
          var pct = Math.max(0, Math.round((1 - (qtyMinPrice[q] / fullAtBase)) * 100));
          // Map back to the option value string
          productData.variants.forEach(function (v) {
            var val = (v.options[qtyOptIdx] || '');
            var vm = val.match(/(\d+)/);
            if (vm && parseInt(vm[1]) === q && pct > 0) {
              savePcts[val] = pct;
            }
          });
        });
      }
    }

    // Current variant — use preselect from card dropdown if available
    var current = productData.currentVariant || productData.variants[0];
    if (preselect) {
      var psLower = preselect.toLowerCase().trim();
      // Match against variant title (e.g. "12 Pack")
      var preselectMatch = productData.variants.find(function (v) {
        return v.title && v.title.toLowerCase().trim() === psLower;
      });
      // Also try matching against individual option values
      if (!preselectMatch) {
        preselectMatch = productData.variants.find(function (v) {
          return v.options && v.options.some(function (o) {
            return o && o.toLowerCase().trim() === psLower;
          });
        });
      }
      if (preselectMatch) {
        current = preselectMatch;
        console.log('[QuickView] Preselected variant:', current.title, formatMoney(current.price));
      }
    }

    // Build HTML
    var html = '';
    optionValues.forEach(function (opt, optIdx) {
      var fieldId = 'qv-option-' + optIdx;
      html += '<div class="qv-variant-group" data-option-index="' + optIdx + '">';
      html += '<label class="qv-variant-label">' + opt.name + '</label>';
      html += '<div class="qv-variant-pills">';
      opt.values.forEach(function (val, valIdx) {
        var inputId = fieldId + '-' + valIdx;
        var isChecked = current.options && current.options[optIdx] === val;
        var badge = savePcts[val] ? '<span class="qv-save-badge">SAVE ' + savePcts[val] + '%</span>' : '';
        html += '<div class="qv-pill-wrap">';
        html += '<input type="radio" id="' + inputId + '" name="' + fieldId + '" value="' + val + '"' + (isChecked ? ' checked' : '') + ' class="qv-pill-radio" />';
        html += '<label for="' + inputId + '" class="qv-pill">' + val + '</label>';
        html += badge;
        html += '</div>';
      });
      html += '</div></div>';
    });

    // Find insertion point: before the buy buttons / form, or after price
    var pickerEl = document.createElement('div');
    pickerEl.className = 'qv-variant-picker-custom';
    pickerEl.innerHTML = html;

    var infoContainer = container.querySelector('.product__info-container, section[id^="ProductInfo"]');
    if (!infoContainer) infoContainer = container;
    console.log('[QuickView] Info container found:', !!infoContainer, infoContainer ? infoContainer.className : 'N/A');

    // Try to insert before the form / buy buttons
    var buyBtn = infoContainer.querySelector('.quantity-button-flex-wrapper, form[action*="/cart/add"], .product-form__buttons');
    // Or after price
    var priceEl = infoContainer.querySelector('[id^="price-"], .price');
    // Or after shipping
    var taxEl = infoContainer.querySelector('.product__tax');

    console.log('[QuickView] Insertion anchors — tax:', !!taxEl, 'price:', !!priceEl, 'buyBtn:', !!buyBtn);

    if (taxEl && taxEl.parentNode) {
      taxEl.parentNode.insertBefore(pickerEl, taxEl.nextSibling);
    } else if (priceEl && priceEl.parentNode) {
      // Insert after the price block (which might include installment form)
      var afterPrice = priceEl.nextElementSibling;
      while (afterPrice && (afterPrice.classList.contains('product__tax') || afterPrice.classList.contains('installment'))) {
        afterPrice = afterPrice.nextElementSibling;
      }
      if (afterPrice) {
        afterPrice.parentNode.insertBefore(pickerEl, afterPrice);
      } else {
        priceEl.parentNode.appendChild(pickerEl);
      }
    } else if (buyBtn && buyBtn.parentNode) {
      buyBtn.parentNode.insertBefore(pickerEl, buyBtn);
    } else {
      infoContainer.appendChild(pickerEl);
    }

    console.log('[QuickView] Built variant picker:', options.length, 'options,', productData.variants.length, 'variants');

    // If preselect changed the current variant, sync price + hidden ID now
    if (preselect && current !== (productData.currentVariant || productData.variants[0])) {
      // Update hidden variant ID inputs
      container.querySelectorAll('input[name="id"]').forEach(function (inp) {
        inp.value = current.id;
      });
      // Update price displays
      container.querySelectorAll('.price-item--regular, .price-item, .price .money').forEach(function (el) {
        var text = el.textContent.trim();
        if (text.indexOf('$') !== -1 || el.closest('.price__regular')) {
          el.textContent = formatMoney(current.price);
        }
      });
      // Update main image
      if (current.featured_image && current.featured_image.src) {
        var mainImg = container.querySelector('.qv-slider__main img');
        if (mainImg) mainImg.src = current.featured_image.src;
      }
      console.log('[QuickView] Synced preselect — price:', formatMoney(current.price), 'variant ID:', current.id);
    }

    // ── Wire change events ──
    pickerEl.addEventListener('change', function (e) {
      if (e.target.type !== 'radio') return;

      // Collect selected value from each option group
      var selectedOptions = [];
      pickerEl.querySelectorAll('.qv-variant-group').forEach(function (group) {
        var checked = group.querySelector('input[type="radio"]:checked');
        if (checked) selectedOptions.push(checked.value);
      });

      console.log('[QuickView] Selected options:', selectedOptions);
      if (!selectedOptions.length) return;

      // Find matching variant
      var match = productData.variants.find(function (v) {
        if (!v.options || v.options.length !== selectedOptions.length) return false;
        return v.options.every(function (opt, i) { return selectedOptions[i] === opt; });
      });

      // Fallback: title match
      if (!match) {
        var titleStr = selectedOptions.join(' / ');
        match = productData.variants.find(function (v) { return v.title === titleStr; });
      }

      // Fallback: single option match
      if (!match && selectedOptions.length === 1) {
        match = productData.variants.find(function (v) {
          return v.options && v.options.indexOf(selectedOptions[0]) !== -1;
        });
      }

      if (match) {
        console.log('[QuickView] Matched variant:', match.title, formatMoney(match.price));

        // Update hidden variant ID
        container.querySelectorAll('input[name="id"]').forEach(function (inp) {
          inp.value = match.id;
        });

        // Update price
        container.querySelectorAll('.price-item--regular, .price-item, .price .money').forEach(function (el) {
          var text = el.textContent.trim();
          if (text.indexOf('$') !== -1 || el.closest('.price__regular')) {
            el.textContent = formatMoney(match.price);
          }
        });

        // Update main image
        if (match.featured_image && match.featured_image.src) {
          var mainImg = container.querySelector('.qv-slider__main img');
          if (mainImg) mainImg.src = match.featured_image.src;
        }

        // Dispatch for Seal or other listeners
        container.dispatchEvent(new CustomEvent('variant:changed', {
          bubbles: true, detail: { variant: match }
        }));
      }
    });
  }

  /* ── Seal Subscriptions — Extract, Inject, Refresh ── */
  function initSealWidget(container, productData, sealShellClone) {
    console.log('[QuickView] initSealWidget — starting, shell provided:', !!sealShellClone);

    // ── Step 1: Use the pre-cloned shell, or build from scratch ──
    var sealShell = sealShellClone || null;

    // ── Step 2: Fallback — build the shell from product JSON ──
    if (!sealShell && productData && productData.variants.length) {
      console.log('[QuickView] Building Seal shell from product JSON');

      // We need the full product object with selling_plan data
      // The .js endpoint includes this
      sealShell = document.createElement('div');
      sealShell.className = 'sealsubs-target-element';
      sealShell.setAttribute('data-seal-extension', '');

      // We need product ID and handle — extract from productData or URL
      var prodId = productData.variants[0].product_id || '';
      var prodHandle = '';

      // Try to get handle from container's existing data or URL
      var existingWrap = container.querySelector('[data-handle]');
      if (existingWrap) prodHandle = existingWrap.getAttribute('data-handle');
      if (!prodHandle) {
        // Extract from the product URL
        var urlMatch = container.closest('.qv-content')
          ? document.querySelector('.qv-full-link')
          : null;
        if (urlMatch) {
          var href = urlMatch.getAttribute('href') || '';
          var handleMatch = href.match(/\/products\/([^/?#]+)/);
          if (handleMatch) prodHandle = handleMatch[1];
        }
      }

      if (prodId) sealShell.setAttribute('data-product-id', prodId);
      if (prodHandle) sealShell.setAttribute('data-handle', prodHandle);
    }

    if (!sealShell) {
      console.warn('[QuickView] Could not create Seal shell — no product data');
      return;
    }

    // ── Step 3: Inject into the modal DOM ──
    // Position: after variant picker, before buy button
    var infoContainer = container.querySelector('.product__info-container, section[id^="ProductInfo"]');
    if (!infoContainer) infoContainer = container;

    var buyBtn = infoContainer.querySelector('.quantity-button-flex-wrapper, form[action*="/cart/add"], .product-form__buttons');
    var variantPicker = infoContainer.querySelector('.qv-variant-picker-custom');

    // Remove any existing Seal div that survived from the fetched HTML
    container.querySelectorAll('.sealsubs-target-element, [class*="sealsubs"]').forEach(function (el) {
      el.remove();
    });

    // Insert after variant picker, or before buy button
    if (variantPicker && variantPicker.nextSibling) {
      variantPicker.parentNode.insertBefore(sealShell, variantPicker.nextSibling);
    } else if (buyBtn && buyBtn.parentNode) {
      buyBtn.parentNode.insertBefore(sealShell, buyBtn);
    } else {
      infoContainer.appendChild(sealShell);
    }

    console.log('[QuickView] Seal shell injected into modal');

    // ── Step 4: Trigger Seal to populate the widget ──
    var triggerSeal = function () {
      // Primary: SealSubs.hardRefresh()
      if (window.SealSubs && typeof window.SealSubs.hardRefresh === 'function') {
        try {
          console.log('[QuickView] Calling SealSubs.hardRefresh()');
          window.SealSubs.hardRefresh();
          return true;
        } catch (e) {
          console.warn('[QuickView] SealSubs.hardRefresh() error:', e.message);
        }
      }
      // Fallback: SealSubs.refresh()
      if (window.SealSubs && typeof window.SealSubs.refresh === 'function') {
        try {
          console.log('[QuickView] Calling SealSubs.refresh()');
          window.SealSubs.refresh();
          return true;
        } catch (e) {
          console.warn('[QuickView] SealSubs.refresh() error:', e.message);
        }
      }
      return false;
    };

    // Staggered attempts — Seal may need a moment to recognize the new DOM
    triggerSeal();
    setTimeout(triggerSeal, 300);
    setTimeout(triggerSeal, 800);
    setTimeout(triggerSeal, 1500);
    setTimeout(function () {
      // Last resort: reset flags and try again
      window.sealsubsloaded = false;
      triggerSeal();
    }, 3000);
  }

  /* ── Auto-select Subscribe & Save ── */
  function autoSelectSubscribe(container) {
    var attempts = 0;
    var interval = setInterval(function () {
      if (++attempts > 40) { clearInterval(interval); return; }

      var seal = container.querySelector('.sealsubs-target-element');
      if (!seal) return;

      // Look for the Subscribe option container (second .sls-option-container)
      var options = seal.querySelectorAll('.sls-option-container');
      if (options.length >= 2) {
        var subOption = options[1];
        if (!subOption.classList.contains('sls-active')) {
          // Click the radio inside Subscribe & Save
          var radio = subOption.querySelector('input[type="radio"][data-type="subscription"]');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            radio.click();
          }
          // Also click the container row (Seal uses click handlers on the row)
          var clickTarget = subOption.querySelector('.sls-selling-plan-group-first-line[role="radio"]');
          if (clickTarget) clickTarget.click();
          else subOption.click();
        }
        clearInterval(interval);
        return;
      }

      // Fallback: any subs radio
      var radios = seal.querySelectorAll('input[type="radio"]');
      if (radios.length >= 2 && !radios[1].checked) {
        radios[1].checked = true;
        radios[1].dispatchEvent(new Event('change', { bubbles: true }));
        radios[1].click();
        clearInterval(interval);
      }
    }, 200);
  }

  /* ── Junip Reviews Re-init ── */
  function reinitJunip() {
    if (window.junip && typeof window.junip.init === 'function') {
      try { window.junip.init(); } catch (e) {}
    }
    if (window.jnp && typeof window.jnp.init === 'function') {
      try { window.jnp.init(); } catch (e) {}
    }
  }

  /* ── Flavor Links (intercept for modal navigation) ── */
  function interceptFlavorLinks(container) {
    container.addEventListener('click', function (e) {
      var link = e.target.closest('.flavor-button, a[href*="/products/"]');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href || href === '#') return;
      // Only intercept product links, not external
      if (href.indexOf('/products/') === -1) return;
      // Don't intercept if it's the active flavor
      if (link.classList.contains('active')) { e.preventDefault(); return; }
      e.preventDefault();
      // Clear cache for this product and reload
      delete cache[href.replace(/\/+$/, '')];
      loadProduct(href);
    });
  }

  /* ── AJAX Cart ── */
  function wireAjaxCart(container) {
    container.addEventListener('submit', async function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();

      var btn = form.querySelector('[type="submit"], .product-form__submit, button[name="add"]');
      var origText = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

      try {
        var fd = new FormData(form);
        if (!fd.get('quantity')) fd.set('quantity', '1');

        var res = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!res.ok) {
          var errData = await res.json().catch(function () { return {}; });
          alert(errData.description || 'Could not add to cart');
          return;
        }

        var item = await res.json();

        // Update cart count badges
        fetch('/cart.json').then(function (r) { return r.json(); }).then(function (c) {
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = c.item_count;
            el.setAttribute('data-cart-count', c.item_count);
          });
        });

        // Dispatch event
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { item: item }, bubbles: true
        }));

        if (btn) { btn.textContent = 'Added! ✓'; }

        // Close modal and open cart drawer
        setTimeout(function () {
          closeModal();
          setTimeout(function () {
            var cd = document.querySelector('cart-drawer');
            if (!cd) return;
            fetch(window.Shopify.routes.root + '?section_id=cart-drawer', {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' }
            }).then(function (r) { return r.text(); }).then(function (html) {
              var tmp = document.createElement('div');
              tmp.innerHTML = html;
              var fresh = tmp.querySelector('cart-drawer');
              if (fresh) {
                cd.innerHTML = fresh.innerHTML;
                cd.classList.remove('is-empty');
              }
              if (typeof cd.open === 'function') cd.open();
              else cd.setAttribute('open', '');
            }).catch(function () {
              if (typeof cd.open === 'function') cd.open();
              else cd.setAttribute('open', '');
            });
          }, 100);
        }, 600);

      } catch (err) {
        alert('Could not connect – please try again');
      } finally {
        if (btn) {
          btn.disabled = false;
          setTimeout(function () { if (btn.textContent === 'Adding...') btn.textContent = origText; }, 3000);
        }
      }
    });
  }

  /* ══════════════════════════════════════════
     MAIN: Fetch & Render
     ══════════════════════════════════════════ */

  async function loadProduct(productUrl, preselect) {
    var overlay = document.querySelector(OVERLAY_SEL);
    if (!overlay) return;

    openModal();

    var loader = overlay.querySelector('.qv-loader');
    var fullLink = overlay.querySelector('.qv-full-link');
    var contentEl = overlay.querySelector('.qv-content');

    if (loader) loader.classList.remove('hidden');
    if (fullLink) fullLink.href = productUrl;

    var cleanUrl = productUrl.replace(/\/+$/, '');

    try {
      // ── 1. FETCH ──
      var html = cache[cleanUrl];
      if (!html) {
        var res = await fetch(cleanUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });
        if (!res.ok) throw new Error('Fetch failed: ' + res.status);
        html = await res.text();
        cache[cleanUrl] = html;
      }

      // ── 2. PARSE ──
      var doc = new DOMParser().parseFromString(html, 'text/html');

      // ── 3. EXTRACT PRODUCT DATA ──
      // Always fetch from .js endpoint first — it's reliable and has option names
      var productData = await fetchProductJson(cleanUrl);

      // Fallback: try extracting from HTML
      if (!productData || !productData.variants.length) {
        console.log('[QuickView] .js failed, trying HTML extraction...');
        productData = extractProductData(doc);
      }

      // If we have variants but no option names, derive them
      if (productData.variants.length && !productData.options.length) {
        // Try to get option names from the HTML DOM before cleanup
        var optNames = [];
        doc.querySelectorAll('[data-option-name]').forEach(function (el) {
          var name = el.getAttribute('data-option-name');
          if (name && optNames.indexOf(name) === -1) optNames.push(name);
        });
        // Fallback: check variant.option1/option2/option3 existence
        if (!optNames.length) {
          var sample = productData.variants[0];
          if (sample.option1) optNames.push('Option 1');
          if (sample.option2) optNames.push('Option 2');
          if (sample.option3) optNames.push('Option 3');
        }
        productData.options = optNames;
        console.log('[QuickView] Derived option names:', optNames);
      }

      // Fill in images from HTML if .js didn't have them
      if (!productData.images.length) {
        var htmlData = extractProductData(doc);
        if (htmlData.images.length) productData.images = htmlData.images;
      }

      // ── 4. FIND PRODUCT-INFO ──
      var productInfo = doc.querySelector('product-info');
      if (!productInfo) throw new Error('No <product-info> found on page');

      // ── 4b. EXTRACT Seal shell BEFORE defusing/cleaning destroys it ──
      var sealShellClone = null;
      var sealEl = productInfo.querySelector('.sealsubs-target-element[data-product]');
      if (sealEl) {
        sealShellClone = sealEl.cloneNode(false); // shallow clone — just the div + data attrs
        console.log('[QuickView] Extracted Seal shell, handle:', sealShellClone.getAttribute('data-handle'));
      } else {
        console.log('[QuickView] No Seal shell found in fetched HTML');
      }

      // ── 5. DEFUSE all custom elements → plain <div>s ──
      defuseCustomElements(productInfo);

      // ── 6. CLEAN unwanted elements ──
      cleanupElements(productInfo);

      // ── 7. BUILD wrapper (preserves classes from product-info but as a plain div) ──
      if (contentEl) {
        contentEl.innerHTML = '';

        var wrap = document.createElement('div');
        wrap.className = 'qv-product-wrap';
        // Copy product-info's classes (gradient, color-scheme, section-padding, etc.)
        var piClasses = productInfo.className || '';
        if (piClasses) wrap.className += ' ' + piClasses;
        // Copy data attributes
        for (var i = 0; i < productInfo.attributes.length; i++) {
          var attr = productInfo.attributes[i];
          if (attr.name.startsWith('data-')) {
            wrap.setAttribute(attr.name, attr.value);
          }
        }
        wrap.innerHTML = productInfo.innerHTML;
        contentEl.appendChild(wrap);

        // ── 8. FORCE transparent backgrounds ──
        forceTransparentBGs(contentEl);

        // ── 9. BUILD image slider ──
        buildImageSlider(contentEl, productData.images);

        // ── 10. BUILD variant picker from scratch ──
        buildVariantPicker(contentEl, productData, preselect);

        // ── 11. SEAL Subscriptions — extract shell, inject, refresh ──
        initSealWidget(contentEl, productData, sealShellClone);
        autoSelectSubscribe(contentEl);

        // ── 12. Junip reviews ──
        reinitJunip();

        // ── 13. AJAX cart ──
        wireAjaxCart(contentEl);

        // ── 14. Flavor links ──
        interceptFlavorLinks(contentEl);
      }

      if (loader) loader.classList.add('hidden');

    } catch (err) {
      console.error('[QuickView] Error:', err);
      if (contentEl) {
        contentEl.innerHTML = '<p style="padding:2rem;text-align:center;color:#0D3635;">' +
          'Could not load product. <a href="' + cleanUrl + '" style="color:#0D3635;text-decoration:underline;">View page →</a></p>';
      }
      if (loader) loader.classList.add('hidden');
    }
  }

  /* ── Global Event Listeners ── */

  document.addEventListener('click', function (e) {
    // Open modal
    var trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();

      // Grab selected variant from the card's pack dropdown
      var preselect = null;
      var card = trigger.closest('.pg-card');
      if (card) {
        var sel = card.querySelector('select.pg-pack-select, [data-pack-select]');
        if (sel && sel.selectedIndex >= 0) {
          preselect = sel.options[sel.selectedIndex].text.trim();
          console.log('[QuickView] Preselect from card:', preselect);
        }
      }

      loadProduct(trigger.getAttribute('data-quick-view'), preselect);
      return;
    }
    // Close on overlay click
    var overlay = e.target.closest(OVERLAY_SEL);
    if (overlay && e.target === overlay) { closeModal(); return; }
    // Close button
    if (e.target.closest('.qv-close')) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var o = document.querySelector(OVERLAY_SEL);
      if (o && o.classList.contains('is-open')) closeModal();
    }
  });
})();